import React, { useMemo, useState } from 'react';

const displayName = (person) =>
  [person.first_name, person.last_name].filter(Boolean).join(' ').trim() ||
  person.email ||
  'Unnamed';

const sortByName = (a, b) =>
  displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' });

const GroupsDerivedGuardiansPanel = ({
  data = null,
  loading = false,
  error = null,
  canOpenMemberProfiles = false,
  canViewMemberDetails = false,
  returnUrlBase = '/groups',
  onSelectGroupPeriod = null
}) => {
  const [search, setSearch] = useState('');
  const [gpFilter, setGpFilter] = useState('all');

  const guardians = data?.guardians || [];
  const groupsPeriods = data?.groups_periods || [];

  const filtered = useMemo(() => {
    let list = [...guardians].sort(sortByName);
    if (gpFilter !== 'all') {
      list = list.filter((g) =>
        (g.groups_periods || []).some(
          (gp) => String(gp.groups_periods_id) === String(gpFilter)
        )
      );
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((g) => {
      const name = displayName(g).toLowerCase();
      if (name.includes(q)) return true;
      if (!canViewMemberDetails) return false;
      const email = (g.email || '').toLowerCase();
      const gpHay = (g.groups_periods || [])
        .map((gp) => `${gp.group_name} ${gp.period_title}`)
        .join(' ')
        .toLowerCase();
      return email.includes(q) || gpHay.includes(q);
    });
  }, [guardians, gpFilter, search, canViewMemberDetails]);

  const showSearch = guardians.length > 8;

  return (
    <section className="groups-dms-members groups-derived-guardians">
      <div className="groups-derived-guardians-header">
        <div>
          <h3 className="groups-derived-guardians-title">Guardians</h3>
          <p className="groups-derived-guardians-sub">
            Derived from person links on students under this group’s teams.
            Not a separate membership group.
          </p>
        </div>
        {data && !loading && (
          <div className="groups-derived-guardians-stats">
            <span className="groups-derived-guardians-stat">
              <strong>{data.distinct_guardian_count ?? 0}</strong> guardians
            </span>
            <span className="groups-derived-guardians-stat">
              <strong>{data.student_count ?? 0}</strong> students
            </span>
            <span className="groups-derived-guardians-stat">
              <strong>{groupsPeriods.length}</strong> group-periods
            </span>
          </div>
        )}
      </div>

      {groupsPeriods.length > 0 && (
        <div className="groups-derived-guardians-gp-row">
          <span className="groups-derived-guardians-gp-label">Group-period</span>
          <div className="groups-derived-guardians-gp-chips">
            <button
              type="button"
              className={`groups-derived-guardians-gp-chip${gpFilter === 'all' ? ' is-active' : ''}`}
              onClick={() => setGpFilter('all')}
            >
              All
            </button>
            {groupsPeriods.map((gp) => {
              const label = [gp.group_name, gp.period_title].filter(Boolean).join(' · ');
              return (
                <button
                  key={gp.groups_periods_id}
                  type="button"
                  className={`groups-derived-guardians-gp-chip${
                    gpFilter === String(gp.groups_periods_id) ? ' is-active' : ''
                  }`}
                  onClick={() => setGpFilter(String(gp.groups_periods_id))}
                  title={`${gp.distinct_guardian_count} guardians · ${gp.student_count} students`}
                >
                  {label}
                  <span className="groups-derived-guardians-gp-chip-count">
                    {gp.distinct_guardian_count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="groups-dms-members-toolbar">
        <div className="groups-dms-members-toolbar-end">
          {showSearch && (
            <input
              type="search"
              className="groups-search groups-search--compact groups-dms-members-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guardians…"
            />
          )}
        </div>
      </div>

      <div className="groups-members-body">
        {loading ? (
          <p className="groups-dms-muted groups-members-body-message">Loading guardians…</p>
        ) : error ? (
          <p className="groups-dms-muted groups-members-body-message">{error}</p>
        ) : filtered.length === 0 ? (
          <p className="groups-dms-muted groups-members-body-message">
            {guardians.length === 0
              ? 'No guardians linked to students in this organization yet.'
              : 'No guardians match your search or filter.'}
          </p>
        ) : (
          <div className="groups-dms-member-list">
            {filtered.map((guardian) => {
              const name = displayName(guardian);
              const gpLabels = (guardian.groups_periods || [])
                .map((gp) => [gp.group_name, gp.period_title].filter(Boolean).join(' · '))
                .filter(Boolean);
              const content = (
                <div>
                  <p className="groups-dms-member-name">{name}</p>
                  {canViewMemberDetails && guardian.email && (
                    <p className="groups-dms-member-email">{guardian.email}</p>
                  )}
                  {gpLabels.length > 0 && (
                    <p className="groups-derived-guardian-gps">
                      {gpLabels.join(' · ')}
                      {guardian.linked_student_count > 1 && (
                        <span className="groups-derived-guardian-students">
                          {' '}
                          · {guardian.linked_student_count} students
                        </span>
                      )}
                    </p>
                  )}
                </div>
              );
              if (canOpenMemberProfiles) {
                const href = `/user-profiles?id=${guardian.id}&from=${encodeURIComponent(returnUrlBase)}`;
                return (
                  <a key={guardian.id} href={href} className="groups-dms-member-row">
                    {content}
                  </a>
                );
              }
              return (
                <div
                  key={guardian.id}
                  className="groups-dms-member-row groups-dms-member-row--static"
                  aria-label={name}
                >
                  {content}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default GroupsDerivedGuardiansPanel;
